// src/components/Admin/StudentHomeCmp/StudenthomeGrid.js
import React, { useState, useEffect } from "react";
import {
  InfoStudent,
  RutsPanelStudent,
  SalaActive,
  AssistantStudent,
  InfoSalaCmp,
  ScenesPanel,
  KahootQuizModal,
} from "./StudenthomePortalCmp";
import { TeacherAPI } from "../../../api/teacherAPI";
import { QuizApi } from "../../../api/quiz";
import { TeacherTasksAPI } from "../../../api/teacherTasksAPI";
import {
  Button,
  Modal,
  Table,
  Loader,
  Message,
  Icon,
  Header,
} from "semantic-ui-react";
import { StudentTasksPanel } from "./studentTask/StudentTasksPanel";
import { useTranslation } from "react-i18next";
import "./StudenthomeGrid.scss";

const teacherApi = new TeacherAPI();
const quizApi = new QuizApi();
const tasksApi = new TeacherTasksAPI();

export function StudenthomeGrid({
  student,
  accessToken,
  classnumber,
  institutionID,
}) {
  const { t } = useTranslation("students");
  const [selected, setSelected] = useState({
    routeId: null,
    classnumber: null,
    teacherId: null,
  });

  const [activeQuiz, setActiveQuiz] = useState(null);
  const [showQuizModal, setShowQuizModal] = useState(false);

  // permite refrescar el panel de tareas tras cerrar examen
  const [tasksRefreshTick, setTasksRefreshTick] = useState(0);
  const [taskForQuizId, setTaskForQuizId] = useState(null);

  const [studentDetail, setStudentDetail] = useState({
    timeTaken: null,
    rows: [],
  });
  const [loadingResult, setLoadingResult] = useState(false);
  const [showResultModal, setShowResultModal] = useState(false);

  const [row2Bg, setRow2Bg] = useState(null);

  // Helpers de quiz (igual que tenías)
  const buildStudentPayload = (quiz, answers = []) => {
    const teacherQuizId = quiz?.teacherQuizId || quiz?._id || quiz?.id;
    const responses = (answers || []).map((a) => ({
      questionIndex: Number(a?.questionIndex) || 0,
      selected: typeof a?.selected === "string" ? a.selected : "",
      timeTaken: Number(a?.timeTaken) || 0,
    }));
    const timeTaken = Math.round(
      responses.reduce((acc, r) => acc + (Number(r.timeTaken) || 0), 0),
    );
    const totalTime =
      typeof quiz?.totalTime === "number" && quiz.totalTime > 0
        ? quiz.totalTime
        : (Array.isArray(quiz?.questions) ? quiz.questions.length : 0) * 20;
    return { teacherQuizId, responses, timeTaken, totalTime };
  };

  const buildDetailsFromApi = (detailResponse, quiz) => {
    const data = detailResponse?.result;
    if (!data || !Array.isArray(quiz?.questions)) {
      return { timeTaken: null, rows: [] };
    }
    const rows = quiz.questions.map((q, idx) => {
      const wrong = (data.incorrectDetails || []).find(
        (d) => d.question === q.question,
      );
      const selected = wrong ? wrong.selected : q.correct;
      const correctAnswer = q.correct;
      const isCorrect = selected === correctAnswer;
      return {
        questionIndex: idx,
        selected,
        correctAnswer,
        correct: isCorrect,
        timeTaken: undefined,
      };
    });
    return { timeTaken: data.timeTaken, rows };
  };

  const buildDetailsFromSave = (saveResponse, quiz) => {
    const s = saveResponse?.studentQuiz;
    if (!s || !Array.isArray(s.responses) || !Array.isArray(quiz?.questions)) {
      return { timeTaken: null, rows: [] };
    }
    const rows = s.responses.map((r) => {
      const correctAnswer = quiz.questions?.[r.questionIndex]?.correct ?? "";
      const isCorrect =
        typeof r.correct === "boolean"
          ? r.correct
          : r.selected === correctAnswer;
      return {
        questionIndex: r.questionIndex,
        selected: r.selected,
        correctAnswer,
        correct: isCorrect,
        timeTaken: r.timeTaken,
      };
    });
    return { timeTaken: s.timeTaken, rows };
  };

  // Efectos: cargar quiz activo del teacher y resultado previo
  useEffect(() => {
    if (!selected.teacherId) {
      setActiveQuiz(null);
      return;
    }
    (async () => {
      try {
        const { teacher } = await teacherApi.getTeacherById(
          accessToken,
          selected.teacherId,
        );
        const quizId = teacher.quizActive;
        if (quizId) {
          const { quiz } = await quizApi.getQuizById(accessToken, quizId);
          setActiveQuiz({
            teacherQuizId: quizId,
            name: quiz.name,
            questions: quiz.questions,
            totalTime: quiz.totalTime,
          });
        } else {
          setActiveQuiz(null);
        }
      } catch {
        setActiveQuiz(null);
      }
    })();
  }, [accessToken, selected.teacherId]);

  useEffect(() => {
    async function fetchResult() {
      if (!student?._id || !activeQuiz) {
        setStudentDetail({ timeTaken: null, rows: [] });
        return;
      }
      setLoadingResult(true);
      try {
        const detail = await quizApi.getDetailedStudentResult(
          accessToken,
          student._id,
          activeQuiz.teacherQuizId,
        );
        setStudentDetail(buildDetailsFromApi(detail, activeQuiz));
      } catch {
        setStudentDetail({ timeTaken: null, rows: [] });
      } finally {
        setLoadingResult(false);
      }
    }
    fetchResult();
  }, [accessToken, student, activeQuiz]);

  const handleSelectRoute = ({ routeId, classnumber, teacherId }) => {
    setSelected({ routeId, classnumber, teacherId });
    setRow2Bg(null);
  };

  // Lanza Kahoot desde una Tarea Programada
  const openQuizFromTask = async ({ teacherQuizId, taskId }) => {
    try {
      const { quiz } = await quizApi.getQuizById(accessToken, teacherQuizId);
      setActiveQuiz({
        teacherQuizId,
        name: quiz.name,
        questions: quiz.questions,
        totalTime: quiz.totalTime,
      });
      setTaskForQuizId(taskId || null);
      setShowQuizModal(true);
    } catch (e) {
      console.error("No se pudo cargar el quizz:", e);
    }
  };

  // Recibir respuestas del modal → guardar → leer detalle → marcar done si aplica
  const handleQuizSubmit = async (answers, quizObj) => {
    try {
      const quizToUse = quizObj || activeQuiz;
      const payload = buildStudentPayload(quizToUse, answers);

      const saveRes = await quizApi.submitStudentQuiz(
        accessToken,
        student._id,
        payload,
      );

      let detail;
      try {
        detail = await quizApi.getDetailedStudentResult(
          accessToken,
          student._id,
          payload.teacherQuizId,
        );
      } catch {
        detail = null;
      }

      const built =
        detail && detail.result
          ? buildDetailsFromApi(detail, quizToUse)
          : buildDetailsFromSave(saveRes, quizToUse);

      setStudentDetail(built);

      // Si venimos de una tarea, marcar examen final como hecho
      try {
        if (taskForQuizId) {
          await tasksApi.markFinalQuizDone(
            accessToken,
            student._id,
            taskForQuizId,
            true,
          );
          // dispara un "tick" para que el panel refresque
          setTasksRefreshTick((x) => x + 1);
        }
      } catch (e) {
        console.warn("Examen guardado, pero no se pudo marcar como done:", e);
      } finally {
        setTaskForQuizId(null);
      }

      setShowQuizModal(false);
      setShowResultModal(true);
    } catch (err) {
      console.error("Error enviando/leyendo resultado del quiz:", err);
      setShowQuizModal(false);
    }
  };

  const rows = Array.isArray(studentDetail.rows) ? studentDetail.rows : [];
  const getCorrectAnswerForRow = (r) => r?.correctAnswer ?? "-";
  const isRowCorrect = (r) =>
    typeof r?.correct === "boolean"
      ? r.correct
      : (r?.selected || "") === (r?.correctAnswer || "");

  return (
    <div className="student-home-grid">
      <div className="row">
        <div className="col col-left">
          <InfoStudent accessToken={accessToken} student={student} />
        </div>

        <div className="col col-middle">
          {!selected.routeId ? (
            <div className="intro-sections">
              <div className="intro-section rutas-creadas">
                <h2 className="intro-title">
                  {t("home.greetingPrefix")} {student.name}
                  {t("home.greetingSuffix")}
                </h2>
                <p className="intro-description">
                  {t("home.introAssistant")} <br />
                  {t("home.introEvaluation")} <br />
                  {t("home.introEnd")}
                </p>
                <hr className="intro-divider" />
              </div>
            </div>
          ) : (
            <>
              <div>
                <div className="row row1">
                  <SalaActive
                    routeId={selected.routeId}
                    accessToken={accessToken}
                    onSceneBanner={row2Bg}
                  />
                  <InfoSalaCmp
                    routeId={selected.routeId}
                    accessToken={accessToken}
                  />

                  {activeQuiz && (
                    <div className="quiz-launcher">
                      {loadingResult ? (
                        <Loader inline size="small" />
                      ) : rows.length > 0 ? (
                        <Button onClick={() => setShowResultModal(true)}>
                          <Icon name="chart bar outline" />{" "}
                          {t("home.viewResultButton")}
                        </Button>
                      ) : (
                        <Button onClick={() => setShowQuizModal(true)}>
                          <Icon name="question circle outline" />{" "}
                          {t("home.evaluationButton")}
                        </Button>
                      )}
                    </div>
                  )}
                </div>

                <div className="row row2">
                  <AssistantStudent
                    routeId={selected.routeId}
                    accessToken={accessToken}
                  />
                </div>

                <div className="row row3">
                  <ScenesPanel
                    routeId={selected.routeId}
                    classnumber={selected.classnumber}
                    accessToken={accessToken}
                  />
                </div>
              </div>
            </>
          )}
        </div>

        <div className="col col-right">
          <RutsPanelStudent
            accessToken={accessToken}
            institutionId={institutionID}
            classNumber={classnumber}
            onSelectRoute={handleSelectRoute}
          />
        </div>
      </div>

      {/* RESULTADO DEL EXAMEN */}
      <Modal
        open={showResultModal}
        onClose={() => setShowResultModal(false)}
        size="small"
        closeIcon
      >
        <Header icon="chart bar outline" content={t("home.resultModalTitle")} />
        <Modal.Content>
          {loadingResult ? (
            <Loader active inline="centered" />
          ) : rows.length > 0 ? (
            <Table celled striped>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>#</Table.HeaderCell>
                  <Table.HeaderCell>{t("home.colSelected")}</Table.HeaderCell>
                  <Table.HeaderCell>{t("home.colCorrect")}</Table.HeaderCell>
                  <Table.HeaderCell>{t("home.colTime")}</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {rows.map((r, i) => {
                  const correctAnswer = getCorrectAnswerForRow(r);
                  const rowIsCorrect = isRowCorrect(r);
                  return (
                    <Table.Row
                      key={i}
                      positive={rowIsCorrect}
                      negative={!rowIsCorrect}
                    >
                      <Table.Cell>{(r.questionIndex ?? i) + 1}</Table.Cell>
                      <Table.Cell>
                        {r.selected ?? t("item.noAnswer")}
                      </Table.Cell>
                      <Table.Cell>{correctAnswer ?? "-"}</Table.Cell>
                      <Table.Cell>{r.timeTaken ?? "-"}</Table.Cell>
                    </Table.Row>
                  );
                })}
              </Table.Body>
            </Table>
          ) : (
            <Message info>
              <Message.Header>{t("home.noResultsHeader")}</Message.Header>
              <p>{t("home.noResultsBody")}</p>
            </Message>
          )}
        </Modal.Content>
        <Modal.Actions>
          <Button color="blue" onClick={() => setShowResultModal(false)}>
            <Icon name="check" /> {t("home.closeButton")}
          </Button>
        </Modal.Actions>
      </Modal>

      {/* MODAL KAHOOT: siempre montado, también para tareas programadas */}
      <KahootQuizModal
        open={showQuizModal && !!activeQuiz}
        onClose={() => setShowQuizModal(false)}
        quiz={activeQuiz}
        onQuizSubmit={handleQuizSubmit}
      />

      {/* TAREAS PROGRAMADAS */}
      <StudentTasksPanel
        accessToken={accessToken}
        studentId={student._id}
        onStartQuiz={openQuizFromTask}
        refreshKey={tasksRefreshTick}
      />
    </div>
  );
}
